import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRoute } from '../lib/router';
import {
  Search, Users, Phone, Mail, Edit2, Trash2,
  UserCheck, UserX, KeyRound, Copy, CheckCheck, LogIn,
  Upload, Camera, CreditCard, ExternalLink, UserCog,
} from 'lucide-react';
import {
  Card, Button, Input, Select,
  Badge, EmptyState, Modal, Label, ConfirmDeleteModal,
} from '../components/ui';
import { useFirm } from '../contexts/FirmContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { listEmployees, createEmployee, updateEmployee, deleteEmployee, listFirmRoles, updateUserStatus, uploadGhanaCard } from '../lib/db';
import { supabase } from '../services/supabaseClient';
import { fullName } from '../lib/formatters';
import { PlanGate } from '../contexts/PlanContext';
import { useActivityLogger } from '../lib/useActivityLogger';
import toast from '../lib/toast';

// ─── Constants ───────────────────────────────────────────────────────────────

// Colour map for role badges — falls back to slate for unknown names
const ROLE_TONE = {
  Admin: 'red', Partner: 'indigo', Attorney: 'blue', Associate: 'blue',
  Paralegal: 'amber', Clerk: 'slate', IT: 'green', Other: 'slate',
};

const blank = {
  first_name: '', last_name: '', email: '', phone: '',
  role: '', role_id: '', title: '', hourly_rate: '', notes: '',
  status: 'active',
  grantAccess: false,
  ghana_card_number: '', ghana_card_url: '',
};

// ─── Credential reveal dialog ─────────────────────────────────────────────────

function CredentialDialog({ open, onClose, credential }) {
  const [copied, setCopied] = useState(false);

  const copyAll = () => {
    const text = `Employee ID: ${credential?.employee_id}\nEmail: ${credential?.email}\nTemporary password: ${credential?.password}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  if (!open || !credential) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div role="dialog" aria-label="App access granted" className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center flex-shrink-0">
            <KeyRound size={20} className="text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h2 className="text-base font-semibold">App access granted</h2>
            <p className="text-xs text-slate-500">Share these credentials with the employee. The password is shown only once.</p>
          </div>
        </div>

        <div className="rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 divide-y divide-slate-200 dark:divide-slate-700 text-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-slate-500 text-xs uppercase tracking-wide">Employee ID</span>
            <span className="font-mono font-semibold text-indigo-600 dark:text-indigo-400">{credential.employee_id}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-slate-500 text-xs uppercase tracking-wide">Email</span>
            <span className="font-medium">{credential.email}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-slate-500 text-xs uppercase tracking-wide">Temp. password</span>
            <span className="font-mono font-semibold text-amber-600 dark:text-amber-400">{credential.password}</span>
          </div>
        </div>

        <p className="text-xs text-slate-500 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
          ⚠️ The employee will be forced to change this password when they first log in.
        </p>

        <div className="flex gap-2">
          <Button
            variant="outline"
            icon={copied ? CheckCheck : Copy}
            onClick={copyAll}
            className="flex-1"
          >
            {copied ? 'Copied!' : 'Copy credentials'}
          </Button>
          <Button onClick={onClose} className="flex-1">Done</Button>
        </div>
      </div>
    </div>
  );
}

// ─── Grant App Access modal ───────────────────────────────────────────────────

function GrantAccessModal({ open, onClose, employee, firmId, firmRoles, onGranted }) {
  const [roleId, setRoleId]   = useState('');
  const [saving, setSaving]   = useState(false);
  const [error,  setError]    = useState('');

  useEffect(() => {
    if (open) { setRoleId(''); setError(''); }
  }, [open]);

  const handleGrant = async () => {
    if (!roleId) { setError('Please select an app role.'); return; }
    setSaving(true);
    setError('');
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      let data, fnError;
      try {
        ({ data, error: fnError } = await supabase.functions.invoke('create-employee', {
          body: {
            employee_db_id: employee.id,
            email:          employee.email,
            first_name:     employee.first_name,
            last_name:      employee.last_name,
            firm_id:        firmId,
            role_id:        roleId,
            phone:          employee.phone,
            title:          employee.title,
            hourly_rate:    employee.hourly_rate,
          },
          signal: controller.signal,
        }));
      } finally {
        clearTimeout(timer);
      }

      if (fnError?.message?.includes('aborted') || fnError?.name === 'AbortError') {
        throw new Error('Request timed out. Make sure the create-employee Edge Function is deployed in Supabase.');
      }
      // data?.error has the real message from the function body; fnError.message is always the generic Supabase one
      if (data?.error || fnError) {
        throw new Error(data?.error || fnError?.message || 'Failed to create login');
      }

      onGranted({
        employee_id: data.employee_id,
        email:       employee.email,
        password:    data.generated_password,
        user_id:     data.user_id,
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Grant app access — ${employee?.first_name || ''} ${employee?.last_name || ''}`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleGrant} disabled={saving || !roleId}>
            {saving ? 'Creating login…' : 'Grant access'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          This will create a DocketFlow login for <strong>{employee?.first_name} {employee?.last_name}</strong>.
          A temporary password will be generated — you'll share it with them manually.
        </p>

        <div>
          <Label required>App role</Label>
          <Select
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            options={[
              { value: '', label: 'Select a role…' },
              ...firmRoles.map((r) => ({ value: r.id, label: r.name })),
            ]}
          />
          <p className="text-xs text-slate-500 mt-1">Controls what this employee can see in the app.</p>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ─── Employee form modal ──────────────────────────────────────────────────────

function EmployeeModal({ open, onClose, onSave, initial, firmRoles }) {
  const [form, setForm] = useState(blank);
  const [cardFile,    setCardFile]    = useState(null);
  const [cardPreview, setCardPreview] = useState('');
  const [formErrors,  setFormErrors]  = useState({});
  const cardUploadRef = useRef(null);  // file picker (no capture)
  const cardCameraRef = useRef(null);  // camera capture

  useEffect(() => {
    if (open) {
      setForm(initial
        ? { ...blank, ...initial, hourly_rate: initial.hourly_rate || '', grantAccess: false }
        : { ...blank }
      );
      setCardFile(null);
      setCardPreview(initial?.ghana_card_url || '');
      setFormErrors({});
    }
  }, [open, initial]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleRoleChange = (roleId) => {
    const found = firmRoles.find((r) => r.id === roleId);
    setForm((f) => ({ ...f, role_id: roleId, role: found?.name || '' }));
  };

  const handleCardFile = (file) => {
    if (!file) return;
    setCardFile(file);
    setCardPreview(URL.createObjectURL(file));
  };

  const handleSave = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = 'First name is required.';
    if (!form.phone.trim())      errs.phone      = 'Phone number is required.';
    if (Object.keys(errs).length > 0) { setFormErrors(errs); return; }
    setFormErrors({});
    onSave({ ...form, hourly_rate: form.hourly_rate ? Number(form.hourly_rate) : 0, _cardFile: cardFile });
  };

  const canGrantAccess = !!form.email && !initial?.user_id;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? 'Edit employee' : 'Add employee'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>
            {initial ? 'Save changes' : form.grantAccess ? 'Add & grant access' : 'Add employee'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Basic info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label required>First name</Label>
            <Input value={form.first_name} onChange={(e) => { setField('first_name', e.target.value); setFormErrors((p) => ({ ...p, first_name: undefined })); }} placeholder="Kwame" className={formErrors.first_name ? 'ring-red-400' : ''} />
            {formErrors.first_name && <p className="text-xs text-red-600 mt-1">{formErrors.first_name}</p>}
          </div>
          <div>
            <Label>Last name</Label>
            <Input value={form.last_name} onChange={(e) => setField('last_name', e.target.value)} placeholder="Mensah" />
          </div>

          {/* App role — pulls from firmRoles (RBAC) */}
          <div>
            <Label>Role</Label>
            <Select
              value={form.role_id || ''}
              onChange={(e) => handleRoleChange(e.target.value)}
              options={[
                { value: '', label: 'Select role…' },
                ...firmRoles.map((r) => ({ value: r.id, label: r.name + (r.description ? ` — ${r.description}` : '') })),
              ]}
            />
          </div>

          <div>
            <Label>Job title</Label>
            <Input value={form.title} onChange={(e) => setField('title', e.target.value)} placeholder="e.g. Senior Associate" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="kwame@firm.com" />
          </div>
          <div>
            <Label>Phone <span className="text-red-500">*</span></Label>
            <Input type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => { setField('phone', e.target.value); setFormErrors((p) => ({ ...p, phone: undefined })); }} placeholder="+233 24 000 0000" className={formErrors.phone ? 'ring-red-400' : ''} />
            {formErrors.phone && <p className="text-xs text-red-600 mt-1">{formErrors.phone}</p>}
          </div>
          <div>
            <Label>Hourly rate (GHS)</Label>
            <Input type="number" step="0.01" value={form.hourly_rate} onChange={(e) => setField('hourly_rate', e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <Label>Status</Label>
            <Select
              options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
              value={form.status}
              onChange={(e) => setField('status', e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <Label>Notes</Label>
            <Input value={form.notes} onChange={(e) => setField('notes', e.target.value)} placeholder="Internal notes (not visible to employee)" />
          </div>
        </div>

        {/* Ghana Card */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
            <CreditCard size={13} /> Ghana Card
          </p>
          <div>
            <Label>Ghana Card Number</Label>
            <Input
              value={form.ghana_card_number}
              onChange={(e) => setField('ghana_card_number', e.target.value)}
              placeholder="GHA-XXXXXXXXX-X"
            />
          </div>
          <div>
            <Label>Card image</Label>
            {/* Two separate hidden inputs — browsers ignore runtime capture attribute changes */}
            <input ref={cardUploadRef} type="file" accept="image/*,application/pdf" className="hidden"
              onChange={(e) => { handleCardFile(e.target.files?.[0]); e.target.value = ''; }} />
            <input ref={cardCameraRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => { handleCardFile(e.target.files?.[0]); e.target.value = ''; }} />

            {cardPreview ? (
              <div className="mt-1 flex items-start gap-3">
                {(cardFile || cardPreview.match(/\.(jpg|jpeg|png|webp|gif)(\?|$)/i)) ? (
                  <img
                    src={cardPreview}
                    alt="Ghana card"
                    className="h-24 w-auto rounded-lg border border-slate-200 dark:border-slate-700 object-cover"
                  />
                ) : (
                  <a href={cardPreview} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline">
                    <ExternalLink size={13} /> View current card
                  </a>
                )}
                <div className="flex flex-col gap-1.5 mt-1">
                  <button type="button" onClick={() => cardUploadRef.current?.click()} className="text-xs text-indigo-600 hover:underline text-left">Replace image</button>
                  <button type="button" onClick={() => { setCardFile(null); setCardPreview(''); setField('ghana_card_url', ''); }} className="text-xs text-red-500 hover:underline text-left">Remove</button>
                </div>
              </div>
            ) : (
              <div className="mt-1 flex gap-2">
                <button
                  type="button"
                  onClick={() => cardUploadRef.current?.click()}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                >
                  <Upload size={14} /> Upload
                </button>
                <button
                  type="button"
                  onClick={() => cardCameraRef.current?.click()}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                >
                  <Camera size={14} /> Scan / take photo
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Grant app access toggle — only for new employees with an email */}
        {!initial && (
          <div className={`rounded-lg border p-4 transition ${form.grantAccess ? 'border-indigo-300 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-950/40' : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40'}`}>
            <label className="flex items-center justify-between cursor-pointer gap-3">
              <div className="flex items-center gap-2">
                <KeyRound size={15} className={form.grantAccess ? 'text-indigo-600' : 'text-slate-400'} />
                <div>
                  <p className="text-sm font-medium">Grant app access</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {canGrantAccess
                      ? 'A temporary login will be created. The employee must change their password on first sign-in.'
                      : 'Add an email address above to enable this.'}
                  </p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={form.grantAccess}
                disabled={!canGrantAccess}
                onChange={(e) => setField('grantAccess', e.target.checked)}
                className="w-4 h-4 rounded accent-indigo-600 disabled:opacity-40"
              />
            </label>

            {form.grantAccess && form.role_id && (
              <p className="mt-3 text-xs text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 rounded px-3 py-2">
                They will log in with the <strong>{form.role}</strong> role and its permissions.
                You can adjust permissions later from the <strong>Roles</strong> page.
              </p>
            )}
            {form.grantAccess && !form.role_id && (
              <p className="mt-3 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 rounded px-3 py-2">
                Please select a role above so we know what access to grant.
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Employee card ────────────────────────────────────────────────────────────
// EmployeeCard is kept around because the inline matter-grid still renders
// similar markup; leaving the function in place avoids drift if we ever
// re-introduce the card view. Underscore prefix tells ESLint we know it's
// currently unused.
// eslint-disable-next-line no-unused-vars
function EmployeeCard({ emp, onEdit, onToggleStatus, onDelete, onGrantAccess, canManage, onClick }) {
  const hasLogin = !!emp.user_id;

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center flex-shrink-0 text-indigo-700 dark:text-indigo-300 font-semibold text-sm">
              {(emp.first_name?.[0] || '?') + (emp.last_name?.[0] || '')}
            </div>
            <div>
              <p className="font-medium text-sm">{fullName(emp) || '—'}</p>
              {emp.title && <p className="text-xs text-slate-500">{emp.title}</p>}
            </div>
          </div>
          {canManage && (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => onEdit(emp)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 hover:text-indigo-600" title="Edit">
                <Edit2 size={14} />
              </button>
              <button onClick={() => onToggleStatus(emp)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 hover:text-amber-600" title={emp.status === 'active' ? 'Deactivate' : 'Activate'}>
                {emp.status === 'active' ? <UserX size={14} /> : <UserCheck size={14} />}
              </button>
              <button onClick={() => onDelete(emp)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 hover:text-red-600" title="Remove">
                <Trash2 size={14} />
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {emp.role && <Badge tone={ROLE_TONE[emp.role] || 'slate'}>{emp.role}</Badge>}
          <Badge tone={emp.status === 'active' ? 'green' : 'slate'}>{emp.status}</Badge>
          {hasLogin && <Badge tone="indigo">Has login</Badge>}
        </div>

        <div className="space-y-1 text-xs text-slate-500">
          {emp.employee_id && (
            <p className="flex items-center gap-1.5 font-mono text-indigo-600 dark:text-indigo-400 font-medium">
              {emp.employee_id}
            </p>
          )}
          {emp.email && <p className="flex items-center gap-1.5"><Mail size={11} />{emp.email}</p>}
          {emp.phone && <p className="flex items-center gap-1.5"><Phone size={11} />{emp.phone}</p>}
          {emp.hourly_rate > 0 && <p>Rate: GHS {Number(emp.hourly_rate).toFixed(2)}/hr</p>}
        </div>

        {/* Grant login — shown only when no login yet and user can manage */}
        {!hasLogin && canManage && emp.email && (
          <div className="pt-2 border-t border-slate-100 dark:border-slate-800" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => onGrantAccess(emp)}
              className="w-full flex items-center justify-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 py-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-950 transition"
            >
              <LogIn size={12} />
              Grant app access
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

function EmployeesPageInner() {
  const { firmId, refreshSignal } = useFirm();
  const { user }                  = useAuth();
  const { can }                   = usePermissions();
  const log                       = useActivityLogger();
  const canManage                 = can('team.manage');
  const [, navigate]              = useRoute();

  const [employees,  setEmployees]  = useState([]);
  const [firmRoles,  setFirmRoles]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [q,          setQ]          = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [showModal,  setShowModal]  = useState(false);
  const [editing,    setEditing]    = useState(null);

  // Grant access state
  const [deletingEmp,   setDeletingEmp]   = useState(null);
  const [grantTarget,   setGrantTarget]   = useState(null);
  const [credential,    setCredential]    = useState(null);
  const [showCred,      setShowCred]      = useState(false);

  useEffect(() => {
    if (!firmId) { setLoading(false); return; }
    Promise.all([
      listEmployees(firmId).then(({ data }) => setEmployees(data || [])),
      listFirmRoles(firmId).then(({ data }) => setFirmRoles(data || [])),
    ]).finally(() => setLoading(false));
  }, [firmId, refreshSignal]);

  const filtered = useMemo(() => {
    // All employees can see the full list — only admin actions are restricted
    return employees
      .filter((e) => !roleFilter   || e.role === roleFilter)
      .filter((e) => !statusFilter || e.status === statusFilter)
      .filter((e) => {
        if (!q) return true;
        const s = q.toLowerCase();
        return fullName(e).toLowerCase().includes(s) ||
          e.email?.toLowerCase().includes(s) ||
          e.phone?.toLowerCase().includes(s) ||
          e.role?.toLowerCase().includes(s) ||
          e.title?.toLowerCase().includes(s);
      });
  }, [employees, q, roleFilter, statusFilter]);

  // What counts as a "lawyer" vs. "support" role in the KPI strip.
  // Lawyers are the ones who can be set as a matter's responsible
  // attorney — that's the test that matters operationally.
  // Admin and IT are NOT lawyers (they're operations roles). Paralegal,
  // Clerk, and all the legacy text-only roles fall under support.
  const LAWYER_ROLES = ['Partner', 'Attorney', 'Associate', 'Of Counsel'];
  const counts = useMemo(() => ({
    total:    employees.length,
    active:   employees.filter((e) => e.status === 'active').length,
    lawyers:  employees.filter((e) => LAWYER_ROLES.includes(e.role)).length,
    support:  employees.filter((e) => !LAWYER_ROLES.includes(e.role)).length,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [employees]);

  // Add is now handled exclusively in Settings → Team. Edit remains here so
  // firm admins can update an employee record without leaving the directory.
  const openEdit = (emp) => { setEditing(emp); setShowModal(true); };

  const handleSave = async (payload) => {
    const { grantAccess, role_id, _cardFile, ...empPayload } = payload;

    if (editing) {
      setEmployees((prev) => prev.map((e) => e.id === editing.id ? { ...e, ...empPayload } : e));
      setShowModal(false);
      setEditing(null);
      const { error } = await updateEmployee(editing.id, empPayload);
      if (error) {
        toast.error('Save failed: ' + error.message);
        setEmployees((prev) => prev.map((e) => e.id === editing.id ? editing : e));
        return;
      }
      // Upload Ghana card if a new file was selected
      if (_cardFile) {
        const { url, error: upErr } = await uploadGhanaCard(firmId, 'employees', editing.id, _cardFile);
        if (upErr) { toast.error('Employee saved but card upload failed: ' + upErr.message); }
        else if (url) {
          await updateEmployee(editing.id, { ghana_card_url: url });
          setEmployees((prev) => prev.map((e) => e.id === editing.id ? { ...e, ghana_card_url: url } : e));
        }
      }
    } else {
      const temp = { id: `temp-${Date.now()}`, firm_id: firmId, ...empPayload };
      setEmployees((prev) => [temp, ...prev]);
      setShowModal(false);
      setEditing(null);
      const { data, error } = await createEmployee(firmId, empPayload);
      if (error) {
        toast.error('Failed to add employee: ' + error.message);
        setEmployees((prev) => prev.filter((e) => e.id !== temp.id));
        return;
      }
      let saved = data;
      setEmployees((prev) => prev.map((e) => e.id === temp.id ? saved : e));
      log('employee', saved.id, 'created', `Employee "${empPayload.first_name} ${empPayload.last_name}" added`);

      // Upload Ghana card if provided
      if (_cardFile && saved) {
        const { url, error: upErr } = await uploadGhanaCard(firmId, 'employees', saved.id, _cardFile);
        if (upErr) { toast.error('Employee saved but card upload failed: ' + upErr.message); }
        else if (url) {
          await updateEmployee(saved.id, { ghana_card_url: url });
          saved = { ...saved, ghana_card_url: url };
          setEmployees((prev) => prev.map((e) => e.id === saved.id ? saved : e));
        }
      }

      // If "Grant app access" was checked, call the Edge Function now
      if (grantAccess && saved && empPayload.email) {
        try {
          const ctrl2 = new AbortController();
          const t2 = setTimeout(() => ctrl2.abort(), 15000);
          let fnData, fnError;
          try {
            ({ data: fnData, error: fnError } = await supabase.functions.invoke('create-employee', {
              body: {
                employee_db_id: saved.id,
                email:          empPayload.email,
                first_name:     empPayload.first_name,
                last_name:      empPayload.last_name,
                firm_id:        firmId,
                role_id:        role_id || null,
                phone:          empPayload.phone,
                title:          empPayload.title,
                hourly_rate:    empPayload.hourly_rate,
              },
              signal: ctrl2.signal,
            }));
          } finally {
            clearTimeout(t2);
          }
          if (fnError?.message?.includes('aborted') || fnError?.name === 'AbortError') {
            throw new Error('Request timed out. Make sure the create-employee Edge Function is deployed in Supabase.');
          }
          if (fnData?.error || fnError) throw new Error(fnData?.error || fnError?.message);
          // Show credentials
          setEmployees((prev) => prev.map((e) => e.id === saved.id ? { ...e, user_id: fnData.user_id, employee_id: fnData.employee_id } : e));
          setCredential({ employee_id: fnData.employee_id, email: empPayload.email, password: fnData.generated_password });
          setShowCred(true);
          log('employee', saved.id, 'updated', `App access granted to "${empPayload.first_name} ${empPayload.last_name}"`);
        } catch (err) {
          toast.error('Employee added, but login creation failed: ' + err.message);
        }
      }
    }
  };

  const handleToggleStatus = async (emp) => {
    const next = emp.status === 'active' ? 'inactive' : 'active';
    setEmployees((prev) => prev.map((e) => e.id === emp.id ? { ...e, status: next } : e));
    await updateEmployee(emp.id, { status: next });
    // If this employee has an app login, mirror the status onto their users row
    // so AppWrapper blocks access on their next session check.
    if (emp.user_id) {
      await updateUserStatus(emp.user_id, next);
    }
  };

  const handleDeleteConfirm = async () => {
    await deleteEmployee(deletingEmp.id);
    setEmployees((prev) => prev.filter((e) => e.id !== deletingEmp.id));
    setDeletingEmp(null);
  };

  const handleDelete = (emp) => {
    setDeletingEmp(emp);
  };

  const handleGrantAccess = (emp) => {
    if (!emp.email) { toast.warning('This employee has no email address. Please add one before granting access.'); return; }
    setGrantTarget(emp);
  };

  const handleGranted = (cred) => {
    // Mark employee as having a login in local state + stamp employee_id
    setEmployees((prev) => prev.map((e) =>
      e.id === grantTarget?.id ? { ...e, user_id: cred.user_id, employee_id: cred.employee_id } : e
    ));
    setGrantTarget(null);
    setCredential(cred);
    setShowCred(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Employees</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {counts.active} active · {counts.total} total
          </p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              icon={UserCog}
              onClick={() => navigate('/settings?section=team')}
              title="Add employees, bulk invite, manage roles — all in Settings → Team"
            >
              Manage team in Settings
            </Button>
          </div>
        )}
      </div>

      <Card>
        {/* Filter bar */}
        <div className="flex flex-col sm:flex-row gap-2 p-4 border-b border-slate-100 dark:border-slate-800">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input placeholder="Search by name, role, email…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
          <Select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            options={[{ value: '', label: 'All roles' }, ...firmRoles.map((r) => ({ value: r.name, label: r.name }))]}
          />
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[{ value: 'active', label: 'Active only' }, { value: '', label: 'All statuses' }, { value: 'inactive', label: 'Inactive' }]}
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : !filtered.length ? (
          <EmptyState
            icon={Users}
            title="No employees found"
            description="Employees are added in Settings → Team. This page is the firm-wide directory once they're in."
            action={canManage ? (
              <Button icon={UserCog} onClick={() => navigate('/settings?section=team')}>
                Open Settings → Team
              </Button>
            ) : null}
          />
        ) : (
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((emp) => {
              const hasLogin = !!emp.user_id;
              const isOwn    = user && emp.user_id === user.id;
              const initials = `${emp.first_name?.[0] || ''}${emp.last_name?.[0] || ''}`.toUpperCase() || '?';
              return (
                <div
                  key={emp.id}
                  onClick={() => navigate(`/employees/${emp.id}`)}
                  className="group relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 cursor-pointer hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-700 transition flex flex-col items-center text-center gap-3"
                >
                  {/* Status dot */}
                  <span className={`absolute top-3 right-3 w-2.5 h-2.5 rounded-full ${emp.status === 'active' ? 'bg-green-400' : 'bg-slate-300'}`} title={emp.status} />

                  {/* Avatar */}
                  <div className="w-20 h-20 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center overflow-hidden flex-shrink-0 ring-2 ring-white dark:ring-slate-900 shadow">
                    {emp.profile_photo_url
                      ? <img src={emp.profile_photo_url} alt={fullName(emp)} className="w-full h-full object-cover" />
                      : <span className="text-indigo-700 dark:text-indigo-300 font-bold text-2xl">{initials}</span>
                    }
                  </div>

                  {/* Name + title */}
                  <div className="min-w-0 w-full">
                    <p className="font-semibold text-slate-900 dark:text-white truncate">{fullName(emp) || '—'}</p>
                    {emp.title && <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">{emp.title}</p>}
                    {isOwn && <p className="text-xs text-indigo-500 dark:text-indigo-400 font-medium mt-0.5">You</p>}
                  </div>

                  {/* Role + app badge */}
                  <div className="flex flex-wrap items-center justify-center gap-1.5">
                    {emp.role && <Badge tone={ROLE_TONE[emp.role] || 'slate'}>{emp.role}</Badge>}
                    {hasLogin && (
                      <span className="inline-flex items-center gap-0.5 text-xs text-indigo-600 dark:text-indigo-400 font-medium bg-indigo-50 dark:bg-indigo-900/20 px-1.5 py-0.5 rounded-full">
                        <KeyRound size={10} /> App
                      </span>
                    )}
                  </div>

                  {/* Contact */}
                  <div className="w-full space-y-1 text-xs text-slate-500 dark:text-slate-400">
                    {emp.email && (
                      <p className="truncate flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Mail size={11} className="flex-shrink-0" />
                        <a href={`mailto:${emp.email}`} className="hover:text-indigo-600 hover:underline truncate">{emp.email}</a>
                      </p>
                    )}
                    {emp.phone && (
                      <p className="truncate flex items-center justify-center gap-1">
                        <Phone size={11} className="flex-shrink-0" /> {emp.phone}
                      </p>
                    )}
                    {emp.employee_id && (
                      <p className="font-mono text-indigo-500 dark:text-indigo-400 text-[11px]">{emp.employee_id}</p>
                    )}
                  </div>

                  {/* Bio snippet */}
                  {emp.bio && (
                    <p className="text-xs text-slate-400 dark:text-slate-500 line-clamp-2 w-full text-left italic border-t border-slate-100 dark:border-slate-800 pt-2">
                      {emp.bio}
                    </p>
                  )}

                  {/* Admin actions */}
                  {canManage && (
                    <div
                      className="flex items-center justify-center gap-1 pt-2 border-t border-slate-100 dark:border-slate-800 w-full opacity-0 group-hover:opacity-100 transition"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button onClick={() => openEdit(emp)}
                        className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-indigo-600 transition" title="Edit">
                        <Edit2 size={13} />
                      </button>
                      <button onClick={() => handleToggleStatus(emp)}
                        className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-amber-600 transition"
                        title={emp.status === 'active' ? 'Deactivate' : 'Activate'}>
                        {emp.status === 'active' ? <UserX size={13} /> : <UserCheck size={13} />}
                      </button>
                      {!emp.user_id && emp.email && (
                        <button onClick={() => handleGrantAccess(emp)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-green-600 transition" title="Grant app access">
                          <LogIn size={13} />
                        </button>
                      )}
                      <button onClick={() => handleDelete(emp)}
                        className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-red-500 transition" title="Remove">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Add/Edit employee modal */}
      <EmployeeModal
        open={showModal}
        onClose={() => { setShowModal(false); setEditing(null); }}
        onSave={handleSave}
        initial={editing}
        firmRoles={firmRoles}
      />

      {/* Grant app access modal */}
      {grantTarget && (
        <GrantAccessModal
          open={!!grantTarget}
          onClose={() => setGrantTarget(null)}
          employee={grantTarget}
          firmId={firmId}
          firmRoles={firmRoles}
          onGranted={handleGranted}
        />
      )}

      {/* Credential reveal dialog */}
      <CredentialDialog
        open={showCred}
        onClose={() => { setShowCred(false); setCredential(null); }}
        credential={credential}
      />

      <ConfirmDeleteModal
        open={!!deletingEmp}
        onClose={() => setDeletingEmp(null)}
        loading={false}
        title="Remove employee"
        description={<>Are you sure you want to remove <strong>{fullName(deletingEmp)}</strong> from the firm? This cannot be undone.</>}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}

export default function EmployeesPage() {
  return <PlanGate feature="employees"><EmployeesPageInner /></PlanGate>;
}
